import { ApiBaseName, ApiReducerPath } from '@Enum/index';
import { createApi } from '@reduxjs/toolkit/query/react';
import { User, UserReplacement } from '@Types/index';
import { UserDetails } from '@Types/common/UserDetails';
import { EmergencyContact } from '@Types/common/EmergencyContact';
import { DependentDetails } from '@Types/common/DependentDetails';
import { customBaseQuery } from '@Utils/api/customBaseQuery';
import { RequisitionApproveUser } from '@Types/common/RequisitionApproveUser';

export const userInfoApi = createApi({
  reducerPath: ApiReducerPath.API_GET_USER_INFO,
  baseQuery: customBaseQuery,
  endpoints: (builder) => ({
    getUserInfo: builder.mutation<User, { empNo?: string; empId?: number }>({
      query: (params) => ({
        url: `${ApiBaseName.DICT}/emp`,
        method: 'get',
        params: params,
      }),
    }),
    getReplacementUserByEmpNo: builder.query<UserReplacement, string>({
      query: (empNumber) => ({
        url: `${ApiBaseName.DICT}/emp/replacement`,
        method: 'get',
        params: {
          empNumb: empNumber,
        },
      }),
    }),
    getReplacementsUserList: builder.query<
      UserReplacement[],
      {
        pageSize?: number;
        page: number;
        searchValue: string;
      }
    >({
      query: ({ pageSize = 9, page = 0, searchValue }) => ({
        url: `${ApiBaseName.DICT}/emp/replacements`,
        method: 'get',
        params: { pageSize, page, search: searchValue },
      }),
    }),
    getReassignUserList: builder.query<
      UserReplacement[],
      {
        pageSize?: number;
        page: number;
        searchValue: string;
        excludeEmpNumbers: string[];
      }
    >({
      query: ({ pageSize = 9, page = 0, searchValue, excludeEmpNumbers }) => {
        const params = new URLSearchParams();
        params.append('pageSize', String(pageSize));
        params.append('page', String(page));
        params.append('search', searchValue);
        excludeEmpNumbers.forEach((empNumber) =>
          params.append('excludeEmpNumbers', empNumber)
        );

        return `${ApiBaseName.DICT}/emp/re-assign-employee?${params.toString()}`;
      },
    }),
    getActiveUsersList: builder.query<User[], string | null>({
      query: (empName) => ({
        url: `${ApiBaseName.DICT}/emp/active`,
        method: 'get',
        params: { empName: empName || '' },
      }),
    }),
    getUsersListByName: builder.query<User[], string | null>({
      query: (empName) => ({
        url: `${ApiBaseName.DICT}/emp/list-by-name`,
        method: 'get',
        params: { empName: empName || '' },
      }),
    }),
    verifyEmployee: builder.mutation<User, string>({
      query: (empNo: string) => ({
        url: `${ApiBaseName.DICT}/emp/verify`,
        method: 'get',
        params: { empNo },
      }),
    }),
    getEmergencyContacts: builder.query<EmergencyContact, string>({
      query: (empNumber) => ({
        url: `${ApiBaseName.DICT}/emp/${empNumber}/emergency-contacts`,
        method: 'get',
      }),
    }),
    getDependentDetails: builder.query<DependentDetails[], string>({
      query: (empNumber) => ({
        url: `${ApiBaseName.DICT}/emp/${empNumber}/dependent-details`,
        method: 'get',
      }),
    }),
    getEmployeeDetails: builder.query<UserDetails, string>({
      query: (empNumber) => ({
        url: `${ApiBaseName.DICT}/emp/${empNumber}/detailed`,
        method: 'get',
      }),
    }),
    getEmployeePurchaseOfficers: builder.query<
      Array<RequisitionApproveUser>,
      void
    >({
      query: () => ({
        url: `${ApiBaseName.DICT}/emp/purchase-officer`,
        method: 'get',
      }),
      transformResponse(response: RequisitionApproveUser[]) {
        // remove duplicate items from response
        const result = response.reduce(
          (acc, currItem) => {
            acc[currItem.userId] = currItem;
            return acc;
          },
          {} as { [key: string]: RequisitionApproveUser }
        );

        return Object.values(result);
      },
    }),
    getEmployeePlantEngineers: builder.query<
      Array<RequisitionApproveUser>,
      void
    >({
      query: () => ({
        url: `${ApiBaseName.DICT}/emp/plant-engineer`,
        method: 'get',
      }),
    }),
    getEmployeePlantWorkShopManagers: builder.query<
      Array<RequisitionApproveUser>,
      void
    >({
      query: () => ({
        url: `${ApiBaseName.DICT}/emp/plant-workshop-manager`,
        method: 'get',
      }),
      transformResponse(response: RequisitionApproveUser[]) {
        // remove duplicate items from response
        const result = response.reduce(
          (acc, currItem) => {
            acc[currItem.userId] = currItem;
            return acc;
          },
          {} as { [key: string]: RequisitionApproveUser }
        );

        return Object.values(result);
      },
    }),
    getEmployeeITAdmins: builder.query<Array<RequisitionApproveUser>, number>({
      query: (compId: number) => ({
        url: `${ApiBaseName.DICT}/emp/it-admin`,
        method: 'get',
        params: { compId },
      }),
    }),
  }),
});

export const {
  useGetActiveUsersListQuery,
  useGetEmployeePurchaseOfficersQuery,
  useGetEmployeePlantEngineersQuery,
  useGetEmployeePlantWorkShopManagersQuery,
  useGetEmployeeITAdminsQuery,
  useGetUserInfoMutation,
  useGetUsersListByNameQuery,
  useGetReplacementsUserListQuery,
  useGetReassignUserListQuery,
  useVerifyEmployeeMutation,
  useGetReplacementUserByEmpNoQuery,
  useGetEmergencyContactsQuery,
  useGetDependentDetailsQuery,
  useGetEmployeeDetailsQuery,
} = userInfoApi;
